import React from 'react';
import { styled } from '@glitz/react';
import CartItemViewModel from 'Cart/Models/CartItemViewModel.type';
import { epsilon, zeta, pixelsToUnit, monochromeDark, thin, medium, small } from 'Shared/Style';
import {
  Media,
  DisplayName,
  UnitPrice,
  TotalPrice,
  Discount,
  PriceType,
  Message,
  QuantityField,
  Variant,
  Icon,
} from 'Checkout/FullCart/components';

type PropType = {
  item: CartItemViewModel;
  currency: string;
  isLoading: boolean;
  update: (code: string, quantity: number, previousQuantity: number) => Promise<void>;
  delete: (code: string, previousQuantity: number) => Promise<void>;
};

export default function (props: PropType) {
  function deleteItem() {
    return props.delete(props.item.code, props.item.quantity);
  }

  function updateItem(qty: number) {
    return props.update(props.item.code, qty, props.item.stockCount);
  }

  return (
    <Base>
      <ColumnOne>
        <Media
          productUrl={props.item.url}
          mediaSrc={props.item.imageUrl}
          alt={props.item.displayName}
          isGift={props.item.isGift}
        />
      </ColumnOne>
      <ColumnTwo>
        <styled.Div css={{ display: 'flex', alignItems: 'center' }}>
          <Name displayName={props.item.displayName} productUrl={props.item.url} isGift={props.item.isGift} />
          {!props.item.isGift && (
            <Delete css={{ pointerEvents: props.isLoading ? 'none' : 'auto' }} onClick={deleteItem} />
          )}
        </styled.Div>
        <styled.Div css={{ display: 'flex', marginTop: pixelsToUnit(5) }}>
          <UnitPrice
            css={{ opacity: props.isLoading ? 0.5 : 1.0, display: 'block', fontSize: zeta, flexGrow: 1 }}
            price={props.item.price}
            currency={props.currency}
          />
          {props.item.hasDiscount && (
            <Discount
              css={{ opacity: props.isLoading ? 0.2 : 1.0, fontSize: zeta }}
              price={props.item.totalPrice - props.item.price * props.item.quantity}
              currency={props.currency}
            />
          )}
        </styled.Div>

        <styled.Div css={{ display: 'flex', marginTop: pixelsToUnit(3) }}>
          {!props.item.isGift ? (
            <QuantityField
              value={props.item.quantity}
              variant={Variant.Small}
              maxQuantity={props.item.stockCount}
              update={updateItem}
              tagDesigner={props.item.tagDesigner}
            />
          ) : (
            <QuantityField
              value={props.item.quantity}
              variant={Variant.Small}
              maxQuantity={props.item.stockCount}
              update={updateItem}
              tagDesigner={props.item.tagDesigner}
              hideIncrementDecrement
            />
          )}
          <TotalPrice
            css={{
              fontSize: props.item.hasDiscount ? pixelsToUnit(19) : epsilon,
              letterSpacing: pixelsToUnit(props.item.hasDiscount ? 0.25 : 0.21),
              opacity: props.isLoading ? 0.5 : 1.0,
              display: 'block',
              flexGrow: 1,
              textAlign: 'right',
            }}
            price={props.item.totalPrice}
            currency={props.currency}
            priceType={props.item.hasDiscount ? PriceType.New : PriceType.Regular}
          />
        </styled.Div>
        {props.item.message && <Message css={{ fontSize: zeta, marginTop: medium }} message={props.item.message} />}
      </ColumnTwo>
    </Base>
  );
}

const Base = styled.div({
  border: {
    bottom: {
      color: monochromeDark,
      width: thin,
      style: 'solid',
    },
  },
  display: 'flex',
  padding: {
    y: pixelsToUnit(10),
  },
});

const ColumnOne = styled.div({
  flex: {
    basis: pixelsToUnit(90),
    grow: 1,
  },
  maxWidth: pixelsToUnit(101),
});

const ColumnTwo = styled.div({
  flex: {
    basis: pixelsToUnit(200),
    grow: 2,
  },
  paddingLeft: pixelsToUnit(11),
});

const Name = styled(DisplayName, {
  fontSize: zeta,
  flex: {
    basis: pixelsToUnit(160),
    grow: 2,
  },
  lineHeight: pixelsToUnit(21),
  maxHeight: pixelsToUnit(42),
  overflowY: 'hidden',
});

const Delete = styled(Icon, {
  cursor: 'pointer',
  height: small,
  width: small,
  verticalAlign: 'middle',
  ':active': {
    transform: 'translateY(1px)',
  },
});
